import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiUrl, DEV_TOOLS } from "../../config";
import { FullDetails } from "../../design-system/components/FullDetails";
import { useAuth } from "../../hooks/useAuth";

/** ---- Types that match your backend endpoints ---- */
type TeamMemberElo = { player_id: string; nickname: string; elo: number | null };
type TeamProfileResp = {
  team: { team_id: string; name: string; avatar?: string };
  players: TeamMemberElo[];
  team_avg_elo: number | null;
};

type MapPoolResp = {
  teamId: string;
  teamName: string;
  maps: { map: string; wins?: number; total?: number; winRate: number; sampleSize: number; matchIds?: string[] }[];
  usedMatchIds: string[];
  requestedMatchIds?: string[];
  requestedMatchIdsHash?: string;
  diagnostics?: {
    matchIdsTotal: number;
    statsFetched: number;
    mapsCounted: number;
    skippedNoStats: number;
    skippedNoTeamMatch: number;
  };
};

type StyleResp = {
  teamId: string;
  teamName: string;
  sampleMaps: number | null;
  avgRoundsFor: number | null;
  avgRoundsAgainst: number | null;
  usedMatchIds?: string[];
};

type KeyPlayersResp = {
  teamId: string;
  teamName: string;
  players: {
    player_id?: string | null;
    nickname: string;
    kd: number;
    kpr: number;
    hsPct: number;
    matches: number;
    kills?: number;
    deaths?: number;
    assists?: number;
    adr?: number;
    mvps?: number;
    firstKills?: number;
    entryCount?: number;
    entryWins?: number;
    utilityDamage?: number;
    flashCount?: number;
    enemiesFlashed?: number;
    clutchKills?: number;
    sniperKills?: number;
    rawStats?: Record<string, unknown> | null;
    rawStatsSamples?: number;
  }[];
  usedMatchIds?: string[];
};

type ScheduleItem = {
  id: number;
  date?: string;
  time?: string;
  opponent: string;
  division?: string;
  yourElo?: number | null;
  opponentElo?: number | null;
  opponentTeamId?: string | null;
  status?: string;
  matchLink?: string | null;
};

type UpcomingOpponentsResp = {
  teamId: string;
  seasonId?: string;
  stageId?: string | null;
  championshipId?: string | null;
  items: {
    id: string;
    scheduledAt?: string | null;
    opponentTeamId?: string | null;
    opponentName?: string | null;
    matchLink?: string | null;
  }[];
};

type RecentOpponent = {
  matchId: string;
  startedAt?: string | null;
  opponentTeamId?: string | null;
  opponentName?: string | null;
  matchLink?: string | null;
  score?: string | null;
};

export function OpponentIntelligence() {
  const { user } = useAuth();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const devToolsEnabled = DEV_TOOLS;
  const [activeTab, setActiveTab] = useState<"intel" | "demos">("intel");

  const [queryId, setQueryId] = useState(teamId ?? "");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [profile, setProfile] = useState<TeamProfileResp | null>(null);
  const [mappool, setMappool] = useState<MapPoolResp | null>(null);
  const [style, setStyle] = useState<StyleResp | null>(null);
  const [keys, setKeys] = useState<KeyPlayersResp | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const [recentSummary, setRecentSummary] = useState<any | null>(null);
  const [recentErr, setRecentErr] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<ScheduleItem[] | null>(null);
  const [scheduleErr, setScheduleErr] = useState<string | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleDebug, setScheduleDebug] = useState<any | null>(null);
  const [recentOpponents, setRecentOpponents] = useState<RecentOpponent[] | null>(null);
  const [recentOppErr, setRecentOppErr] = useState<string | null>(null);
  const [recentOppLoading, setRecentOppLoading] = useState(false);
  const [recentOppDebug, setRecentOppDebug] = useState<any | null>(null);
  const [derivedTeamId, setDerivedTeamId] = useState<string | null>(null);
  const [derivedTeamLoading, setDerivedTeamLoading] = useState(false);
  const [derivedTeamErr, setDerivedTeamErr] = useState<string | null>(null);
  const [discoveryDebug, setDiscoveryDebug] = useState<any | null>(null);
  const [discoveryDebugErr, setDiscoveryDebugErr] = useState<string | null>(null);
  const [cacheToken, setCacheToken] = useState("");
  const [cacheClearStatus, setCacheClearStatus] = useState<string | null>(null);
  const [allowDiscoveryFallback, setAllowDiscoveryFallback] = useState(false);
  const [opponentMatchIds, setOpponentMatchIds] = useState<string[]>([]);
  const [mapPoolRequestUrl, setMapPoolRequestUrl] = useState<string | null>(null);
  const [keyPlayersRequestUrl, setKeyPlayersRequestUrl] = useState<string | null>(null);
  const [recentOppLoaded, setRecentOppLoaded] = useState(false);
  const [derivedTeamMeta, setDerivedTeamMeta] = useState<{
    source?: string;
    confidence?: number;
    samples?: number;
    leagueTeamId?: string | null;
    leagueTeamName?: string | null;
    leagueConfidence?: number | null;
    leagueSamples?: number | null;
  } | null>(null);
  const [derivedTeamCached, setDerivedTeamCached] = useState(false);

  const [demoToken, setDemoToken] = useState("");
  const [demoMatchId, setDemoMatchId] = useState("");
  const [demoTeamId, setDemoTeamId] = useState("");
  const [demoMatchUrl, setDemoMatchUrl] = useState("");
  const [demoUrl, setDemoUrl] = useState("");
  const [demoFile, setDemoFile] = useState<File | null>(null);
  const [demoStatus, setDemoStatus] = useState<string | null>(null);
  const [demoItems, setDemoItems] = useState<any[]>([]);
  const [linkDemoId, setLinkDemoId] = useState("");
  const [parseDemoId, setParseDemoId] = useState("");
  const [parseMeta, setParseMeta] = useState<{
    enabled?: boolean;
    attempted?: boolean;
    reason?: string | null;
  } | null>(null);
  const [parseJobMeta, setParseJobMeta] = useState<any | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [demoPurge, setDemoPurge] = useState<Record<string, boolean>>({});
  const [eventItems, setEventItems] = useState<any[]>([]);
  const [roundItems, setRoundItems] = useState<any[]>([]);
  const [mapItems, setMapItems] = useState<any[]>([]);
  const [eventStats, setEventStats] = useState<Array<{ type: string; count: number }>>([]);
  const [eventTotal, setEventTotal] = useState<number | null>(null);
  const [derivedStats, setDerivedStats] = useState<any | null>(null);
  const [positionCount, setPositionCount] = useState<number | null>(null);
  const [heatmapData, setHeatmapData] = useState<any | null>(null);
  const [heatmapPlayerCounts, setHeatmapPlayerCounts] = useState<
    Array<{ playerId: string; count: number }>
  >([]);
  const [heatmapPlayerId, setHeatmapPlayerId] = useState("");
  const [heatmapMapName, setHeatmapMapName] = useState("");
  const [heatmapDetail, setHeatmapDetail] = useState(3);
  const [heatmapLimit, setHeatmapLimit] = useState("50000");
  const [positionDebug, setPositionDebug] = useState<any | null>(null);
  const heatmapCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const demoFileInputRef = useRef<HTMLInputElement | null>(null);
  const heatmapAutoTimerRef = useRef<number | null>(null);
  const heatmapAbortRef = useRef<AbortController | null>(null);
  const lastAutoLoadMatchIdRef = useRef<string | null>(null);
  const [adminUserId, setAdminUserId] = useState("");
  const [adminTeamId, setAdminTeamId] = useState("");

  const teamCacheKey = useMemo(() => {
    const pid = user?.faceit?.playerId;
    return pid ? `faceit_team_cache:${pid}` : null;
  }, [user?.faceit?.playerId]);

  const leagueContextLabel = useMemo(() => {
    if (!recentOppDebug) return null;
    const parts = [
      recentOppDebug?.regionName || recentOppDebug?.regionId,
      recentOppDebug?.divisionName || recentOppDebug?.divisionId,
      recentOppDebug?.stageName || recentOppDebug?.stageId,
      recentOppDebug?.conferenceName || recentOppDebug?.conferenceId,
    ].filter(Boolean);
    return parts.length ? parts.join(" / ") : null;
  }, [recentOppDebug]);

  const currentUserId = useMemo(() => {
    return (user as any)?.id || null;
  }, [user]);

  const heatmapPlayers = useMemo(() => {
    const players = new Map<string, string>();
    const add = (steamId?: string | null, name?: string | null) => {
      if (!steamId || !name) return;
      if (!players.has(steamId)) players.set(steamId, String(name));
    };
    for (const ev of eventItems || []) {
      const meta = ev?.meta || {};
      add(meta.user_steamid, meta.user_name);
      add(meta.attacker_steamid, meta.attacker_name);
      add(meta.assister_steamid, meta.assister_name);
      add(meta.victim_steamid, meta.victim_name);
    }
    return Array.from(players.entries())
      .map(([steamId, name]) => ({ steamId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eventItems]);

  const heatmapPlayersForSelect = useMemo(() => {
    const bySteamId = new Map(heatmapPlayers.map((p) => [p.steamId, p.name]));
    const byCount = new Map(heatmapPlayerCounts.map((p) => [p.playerId, p.count]));
    const merged = new Map<string, { steamId: string; name: string; count: number }>();

    for (const row of heatmapPlayerCounts) {
      const name = bySteamId.get(row.playerId) || row.playerId;
      merged.set(row.playerId, { steamId: row.playerId, name, count: row.count });
    }
    for (const p of heatmapPlayers) {
      if (!merged.has(p.steamId)) {
        merged.set(p.steamId, { steamId: p.steamId, name: p.name, count: 0 });
      }
    }
    return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [heatmapPlayerCounts, heatmapPlayers]);

  useEffect(() => {
    if (teamId) setDemoTeamId(teamId);
  }, [teamId]);

  useEffect(() => {
    if (!parseDemoId || demoMatchId) return;
    const hit = demoItems.find((it) => it?.id === parseDemoId);
    if (hit?.matchId) {
      setDemoMatchId(String(hit.matchId));
    }
  }, [parseDemoId, demoItems, demoMatchId]);

  useEffect(() => {
    if (!adminTeamId && teamId) setAdminTeamId(teamId);
  }, [teamId, adminTeamId]);

  useEffect(() => {
    if (activeTab !== "demos") return;
    if (!demoToken) return;
    if (demoItems.length) return;
    loadDemos();
  }, [activeTab, demoToken, demoItems.length]);

  useEffect(() => {
    if (!adminUserId && currentUserId) setAdminUserId(currentUserId);
  }, [currentUserId, adminUserId]);

  useEffect(() => {
    if (!devToolsEnabled) return;
    if (demoToken) return;
    let active = true;
    fetch(apiUrl("/auth/steam/token"), { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        const token = data?.token || data?.steam_token || data?.steamToken || null;
        if (token) {
          setDemoToken(token);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [devToolsEnabled, demoToken]);

  async function ingestDemo() {
    if (!demoToken || !demoUrl) {
      setDemoStatus("steam_token and demo URL are required.");
      return;
    }
    setDemoStatus("Submitting metadata...");
    try {
      const res = await fetch(apiUrl(`/api/demos/ingest?steam_token=${encodeURIComponent(demoToken)}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matchId: demoMatchId || null,
          matchUrl: demoMatchUrl || null,
          url: demoUrl,
          teamId: demoTeamId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to ingest demo.");
        return;
      }
      setDemoStatus("Metadata ingested.");
      setDemoItems((prev) => [data?.demo, ...prev].filter(Boolean));
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function uploadDemo() {
    if (!demoToken || !demoFile) {
      setDemoStatus("steam_token and a demo file are required.");
      return;
    }
    setDemoStatus("Uploading demo...");
    try {
      const qs = new URLSearchParams();
      if (demoMatchId) qs.set("matchId", demoMatchId);
      if (demoMatchUrl) qs.set("matchUrl", demoMatchUrl);
      if (demoTeamId) qs.set("teamId", demoTeamId);
      qs.set("filename", demoFile.name);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/demos/upload?${qs.toString()}`), {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: demoFile,
      });
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to upload demo.");
        return;
      }
      setDemoStatus("Demo uploaded.");
      setDemoItems((prev) => [data?.demo, ...prev].filter(Boolean));
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  function openDemoPicker() {
    demoFileInputRef.current?.click();
  }

  async function loadDemos() {
    if (!demoToken) {
      setDemoStatus("steam_token is required.");
      return;
    }
    setDemoStatus("Loading demos...");
    try {
      const qs = new URLSearchParams();
      if (demoMatchId) qs.set("matchId", demoMatchId);
      if (!demoMatchId) qs.set("mine", "1");
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/demos?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load demos.");
        return;
      }
      setDemoItems(Array.isArray(data?.items) ? data.items : []);
      setDemoStatus("Loaded.");
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function linkDemo() {
    if (!demoToken || !linkDemoId || !demoMatchId) {
      setDemoStatus("steam_token, demo id, and matchId are required to link.");
      return;
    }
    setDemoStatus("Linking demo...");
    try {
      const res = await fetch(
        apiUrl(`/api/demos/${encodeURIComponent(linkDemoId)}/link?steam_token=${encodeURIComponent(demoToken)}`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matchId: demoMatchId, teamId: demoTeamId || null }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to link demo.");
        return;
      }
      setDemoStatus("Demo linked.");
      setDemoItems((prev) => prev.map((it) => (it?.id === data?.demo?.id ? data.demo : it)));
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function queueParse(demoIdOverride?: string) {
    const demoId = demoIdOverride || parseDemoId;
    if (!demoToken || !demoId) {
      setDemoStatus("steam_token and demo id are required to parse.");
      return;
    }
    setDemoStatus("Queueing parse...");
    setIsParsing(true);
    try {
      const res = await fetch(
        apiUrl(`/api/demos/${encodeURIComponent(demoId)}/parse?steam_token=${encodeURIComponent(demoToken)}`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teamId: demoTeamId || null }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to queue parse.");
        return;
      }
      const autoWorker = data?.autoWorker || null;
      setParseMeta(autoWorker);
      const autoHint = autoWorker?.enabled
        ? autoWorker?.attempted
          ? "Auto worker started."
          : autoWorker?.reason || "Auto worker not started."
        : autoWorker?.reason || "Auto worker not enabled.";
      setDemoStatus(
        `Parse queued${data?.job?.id ? ` (${data.job.id})` : ""}. ${autoHint} Waiting for worker...`
      );
      await pollParseJob(demoId);
    } catch {
      setDemoStatus("Failed to reach backend.");
    } finally {
      setIsParsing(false);
    }
  }

  async function selectDemo(demo: any, opts?: { parseAndLoad?: boolean }) {
    if (!demo) return;
    if (demo.matchId) setDemoMatchId(String(demo.matchId));
    if (demo.teamId) setDemoTeamId(String(demo.teamId));
    setParseDemoId(String(demo.id));
    setLinkDemoId(String(demo.id));
    if (opts?.parseAndLoad) {
      await queueParse(String(demo.id));
    }
  }

  async function deleteDemo(demoId: string, purge?: boolean) {
    if (!demoToken || !demoId) return;
    setDemoStatus("Deleting demo...");
    try {
      const qs = new URLSearchParams();
      qs.set("steam_token", demoToken);
      if (purge) qs.set("purge", "1");
      const res = await fetch(
        apiUrl(`/api/demos/${encodeURIComponent(demoId)}?${qs.toString()}`),
        { method: "DELETE" }
      );
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to delete demo.");
        return;
      }
      setDemoItems((prev) => prev.filter((it) => it?.id !== demoId));
      setDemoStatus(purge ? "Demo + match data deleted." : "Demo deleted.");
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function loadAllDemoData() {
    await loadEvents();
    await loadRounds();
    await loadMaps();
    await loadEventStats();
    await loadDerivedStats();
    await loadPositionStats();
    await loadHeatmap();
  }

  async function loadEvents() {
    if (!demoToken || !demoMatchId) {
      setDemoStatus("steam_token and matchId are required to load events.");
      return;
    }
    setDemoStatus("Loading events...");
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("limit", "200");
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/events?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load events.");
        return;
      }
      setEventItems(Array.isArray(data?.items) ? data.items : []);
      setDemoStatus(`Loaded events: ${Array.isArray(data?.items) ? data.items.length : 0}`);
      await loadEventStats();
      await loadPositionStats();
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function loadDerivedStats() {
    if (!demoToken || !demoMatchId) {
      setDemoStatus("steam_token and matchId are required to load stats.");
      return;
    }
    setDemoStatus("Loading derived stats...");
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/derived-stats?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load stats.");
        return;
      }
      setDerivedStats(data || null);
      await loadPositionStats();
      setDemoStatus("Loaded derived stats.");
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function loadEventStats() {
    if (!demoToken || !demoMatchId) return;
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/events/stats?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        return;
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setEventStats(
        items.map((row: any) => ({
          type: String(row?.type || "unknown"),
          count: Number(row?._count?._all || 0),
        }))
      );
      setEventTotal(typeof data?.total === "number" ? data.total : null);
    } catch {
      // best effort
    }
  }

  async function loadPositionStats() {
    if (!demoToken || !demoMatchId) return;
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/positions/stats?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) return;
      setPositionCount(typeof data?.count === "number" ? data.count : null);
    } catch {
      // best effort
    }
  }

  async function loadPositionDebug() {
    if (!demoToken || !demoMatchId) return;
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/positions/debug?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) return;
      setPositionDebug(data || null);
    } catch {
      // best effort
    }
  }

  async function loadParseJobMeta() {
    if (!demoToken) return;
    const demoId = parseDemoId || linkDemoId || demoItems?.[0]?.id;
    if (!demoId) return;
    try {
      const qs = new URLSearchParams();
      qs.set("demoId", demoId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/parse-jobs?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) return;
      const job = Array.isArray(data?.items) ? data.items[0] : null;
      if (job?.meta) setParseJobMeta(job.meta);
    } catch {
      // best effort
    }
  }

  async function loadHeatmap() {
    if (!demoToken || !demoMatchId) {
      setDemoStatus("steam_token and matchId are required to load heatmap.");
      return;
    }
    setDemoStatus("Loading heatmap...");
    if (heatmapAbortRef.current) {
      heatmapAbortRef.current.abort();
    }
    const controller = new AbortController();
    heatmapAbortRef.current = controller;
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      if (heatmapPlayerId) qs.set("playerId", heatmapPlayerId);
      if (heatmapMapName) qs.set("mapName", heatmapMapName);
      const detailBins = [320, 260, 200, 160, 120];
      const binSize =
        detailBins[Math.max(0, Math.min(detailBins.length - 1, heatmapDetail - 1))];
      const limit = Math.min(50000, Math.max(1000, Number(heatmapLimit || 50000)));
      qs.set("binSize", String(binSize));
      qs.set("limit", String(limit));
      const res = await fetch(apiUrl(`/api/heatmap/positions?${qs.toString()}`), {
        signal: controller.signal,
      });
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load heatmap.");
        return;
      }
      setHeatmapData(data || null);
      setDemoStatus("Loaded heatmap.");
    } catch {
      if (!controller.signal.aborted) {
        setDemoStatus("Failed to reach backend.");
      }
    }
  }

  async function loadHeatmapPlayers() {
    if (!demoToken || !demoMatchId) return;
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/positions/players?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) return;
      setHeatmapPlayerCounts(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // best effort
    }
  }

  useEffect(() => {
    if (!demoToken || !demoMatchId) return;
    if (heatmapAutoTimerRef.current) {
      window.clearTimeout(heatmapAutoTimerRef.current);
    }
    heatmapAutoTimerRef.current = window.setTimeout(() => {
      void loadHeatmap();
    }, 350);
    return () => {
      if (heatmapAutoTimerRef.current) {
        window.clearTimeout(heatmapAutoTimerRef.current);
      }
    };
  }, [demoToken, demoMatchId, heatmapPlayerId, heatmapMapName, heatmapDetail, heatmapLimit]);

  useEffect(() => {
    void loadHeatmapPlayers();
  }, [demoToken, demoMatchId]);

  useEffect(() => {
    const canvas = heatmapCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);
    if (!heatmapData?.bins || !heatmapData?.stats || !heatmapData?.stats?.binsX) return;
    const { bins, stats } = heatmapData;
    const binsX = Math.max(1, stats.binsX);
    const binsY = Math.max(1, stats.binsY);
    const maxCount = Math.max(1, stats.maxCount || 1);
    const cellW = width / binsX;
    const cellH = height / binsY;
    for (const bin of bins) {
      const intensity = Math.min(1, bin.count / maxCount);
      ctx.fillStyle = `rgba(79,195,247,${0.1 + intensity * 0.8})`;
      ctx.fillRect(bin.x * cellW, bin.y * cellH, cellW, cellH);
    }
  }, [heatmapData]);

  async function loadRounds() {
    if (!demoToken || !demoMatchId) {
      setDemoStatus("steam_token and matchId are required to load rounds.");
      return;
    }
    setDemoStatus("Loading rounds...");
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/rounds?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load rounds.");
        return;
      }
      setRoundItems(Array.isArray(data?.items) ? data.items : []);
      setDemoStatus(`Loaded rounds: ${Array.isArray(data?.items) ? data.items.length : 0}`);
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function loadMaps() {
    if (!demoToken || !demoMatchId) {
      setDemoStatus("steam_token and matchId are required to load maps.");
      return;
    }
    setDemoStatus("Loading maps...");
    try {
      const qs = new URLSearchParams();
      qs.set("matchId", demoMatchId);
      qs.set("steam_token", demoToken);
      const res = await fetch(apiUrl(`/api/maps?${qs.toString()}`));
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to load maps.");
        return;
      }
      setMapItems(Array.isArray(data?.items) ? data.items : []);
      setDemoStatus(`Loaded maps: ${Array.isArray(data?.items) ? data.items.length : 0}`);
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  async function pollParseJob(demoId: string) {
    return new Promise<void>((resolve) => {
      const maxAttempts = 15;
      let attempt = 0;
      const tick = async () => {
        attempt += 1;
        try {
          const qs = new URLSearchParams();
          qs.set("demoId", demoId);
          qs.set("steam_token", demoToken);
          const res = await fetch(apiUrl(`/api/parse-jobs?${qs.toString()}`));
          const data = await res.json();
          const job = Array.isArray(data?.items) ? data.items[0] : null;
          if (job?.meta) {
            setParseJobMeta(job.meta);
          }
          if (job?.status === "completed") {
            setDemoStatus("Parse completed. Ready to load results.");
            resolve();
            return;
          }
          if (job?.status === "failed") {
            setDemoStatus(job?.error || "Parse failed.");
            resolve();
            return;
          }
          if (attempt === 1) {
            setDemoStatus("Parse queued. Waiting for worker...");
          }
        } catch {
          // ignore polling errors
        }
        if (attempt < maxAttempts) {
          setTimeout(tick, 1500);
        } else {
          const autoHint = parseMeta?.enabled
            ? parseMeta?.attempted
              ? "Auto worker started."
              : parseMeta?.reason || "Auto worker not started."
            : parseMeta?.reason || "Auto worker not enabled.";
          setDemoStatus(
            `Parse still queued. ${autoHint} If nothing happens, run demo:worker once.`
          );
          resolve();
        }
      };
      setTimeout(tick, 1200);
    });
  }

  async function grantTeamAccess() {
    if (!demoToken || !adminUserId || !adminTeamId) {
      setDemoStatus("steam_token, userId, and teamId are required.");
      return;
    }
    setDemoStatus("Granting team access...");
    try {
      const res = await fetch(
        apiUrl(`/api/admin/users/${encodeURIComponent(adminUserId)}/teams?steam_token=${encodeURIComponent(demoToken)}`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teamId: adminTeamId, role: "OWNER" }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setDemoStatus(data?.error || "Failed to grant access.");
        return;
      }
      setDemoStatus("Team access granted.");
    } catch {
      setDemoStatus("Failed to reach backend.");
    }
  }

  // Load all datasets
  useEffect(() => {
    if (!teamId) {
      setLoading(false);
      setErr("No teamId provided in the route.");
      return;
    }
    const id: string = teamId; // narrow to string for TS
    let mounted = true;
    setLoading(true);
    setErr(null);
    setProfile(null);
    setMappool(null);
    setStyle(null);
    setKeys(null);
    setRecentSummary(null);
    setRecentErr(null);
    setRecentOpponents(null);
    setRecentOppErr(null);
    setRecentOppLoading(true);

    async function load() {
      try {
        const [pRes, stRes, oppRes] = await Promise.all([
          fetch(apiUrl(`/api/faceit/team/${encodeURIComponent(id)}/profile`)),
          fetch(apiUrl(`/api/faceit/team/${encodeURIComponent(id)}/style`)),
          fetch(
            apiUrl(
              `/api/faceit/team/${encodeURIComponent(id)}/opponents?align=1&discover=league&max=6&onlyLeague=1&debug=1`
            )
          ),
        ]);

        const [p, st, opp] = await Promise.all([pRes.json(), stRes.json(), oppRes.json()]);
        if (!mounted) return;

        // Handle any error payloads
        if (!pRes.ok || p?.error) throw new Error(p?.error || `Profile HTTP ${pRes.status}`);
        if (!stRes.ok || st?.error) throw new Error(st?.error || `Style HTTP ${stRes.status}`);
        if (!oppRes.ok || opp?.error) {
          setRecentOppErr(opp?.error || `Opponents HTTP ${oppRes.status}`);
        } else {
          setRecentOpponents(opp?.opponents || []);
          setRecentOppDebug(opp?.debug || null);
        }

        const oppMatchIds = Array.isArray(opp?.opponents)
          ? opp.opponents.map((o: RecentOpponent) => o.matchId).filter(Boolean)
          : [];
        setOpponentMatchIds(oppMatchIds);
        setRecentOppLoaded(true);

        setProfile(p as TeamProfileResp);
        setStyle(st as StyleResp);
      } catch (e: any) {
        setErr(e?.message || "Failed to load intelligence");
      } finally {
        if (mounted) {
          setLoading(false);
          setRecentOppLoading(false);
        }
      }
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    const teamIdValue = teamId;
    let mounted = true;
    setKeys(null);
    setMappool(null);
    setKeyPlayersRequestUrl(null);
    setMapPoolRequestUrl(null);

    async function loadFromOpponents() {
      try {
        const ids = opponentMatchIds;
        if (!recentOppLoaded) return;
        if (ids.length === 0 && !allowDiscoveryFallback) {
          if (!mounted) return;
          setErr("No recent opponent matches available to build map pool.");
          return;
        }
        if (ids.length > 0 && err) {
          setErr(null);
        }
        const kpParams = new URLSearchParams();
        if (ids.length) {
          kpParams.set("matchIds", ids.slice(0, 20).join(","));
        } else if (allowDiscoveryFallback) {
          kpParams.set("discover", "league");
          kpParams.set("max", "20");
        }
        const kpUrl =
          kpParams.toString().length > 0
            ? apiUrl(`/api/faceit/team/${encodeURIComponent(teamIdValue)}/key-players?${kpParams}`)
            : apiUrl(`/api/faceit/team/${encodeURIComponent(teamIdValue)}/key-players`);
        setKeyPlayersRequestUrl(kpUrl);
        const kpRes = await fetch(kpUrl);
        const kp = await kpRes.json();
        if (!kpRes.ok || kp?.error) throw new Error(kp?.error || `Key-players HTTP ${kpRes.status}`);

        const mpParams = new URLSearchParams();
        if (ids.length) {
          mpParams.set("matchIds", ids.slice(0, 20).join(","));
        } else if (allowDiscoveryFallback) {
          mpParams.set("discover", "league");
          mpParams.set("max", "20");
          mpParams.set("onlyLeague", "1");
        }
        const mpUrl =
          mpParams.toString().length > 0
            ? apiUrl(`/api/faceit/team/${encodeURIComponent(teamIdValue)}/map-pool?${mpParams}`)
            : apiUrl(`/api/faceit/team/${encodeURIComponent(teamIdValue)}/map-pool`);
        setMapPoolRequestUrl(mpUrl);
        const mpRes = await fetch(mpUrl);
        const mp = await mpRes.json();
        if (!mpRes.ok || mp?.error) throw new Error(mp?.error || `Map-pool HTTP ${mpRes.status}`);

        if (!mounted) return;
        setMappool(mp as MapPoolResp);
        setKeys(kp as KeyPlayersResp);
      } catch (e: any) {
        if (!mounted) return;
        setErr(e?.message || "Failed to load map pool / key players");
      }
    }

    void loadFromOpponents();
    return () => {
      mounted = false;
    };
  }, [teamId, opponentMatchIds, allowDiscoveryFallback]);

  useEffect(() => {
    if (!teamId) return;
    let mounted = true;
    setDiscoveryDebugErr(null);
    fetch(apiUrl(`/api/faceit/team/${encodeURIComponent(teamId)}/discovery-debug?stats=1&max=20`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        if (!ok || j?.error) setDiscoveryDebugErr(j?.error || "Failed to load discovery debug");
        else setDiscoveryDebug(j);
      })
      .catch(() => mounted && setDiscoveryDebugErr("Failed to load discovery debug"))
      .finally(() => {});
    return () => {
      mounted = false;
    };
  }, [teamId]);

  useEffect(() => {
    if (!showRecent || !teamId) return;
    let mounted = true;
    setRecentErr(null);
    fetch(apiUrl(`/api/faceit/team/${encodeURIComponent(teamId)}/recent-summary?max=20`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        if (!ok || j?.error) setRecentErr(j?.error || "Failed to load recent matches");
        else setRecentSummary(j);
      })
      .catch(() => mounted && setRecentErr("Failed to load recent matches"))
      .finally(() => {});
    return () => {
      mounted = false;
    };
  }, [showRecent, teamId]);

  useEffect(() => {
    if (!teamId) return;
    let mounted = true;
    setScheduleLoading(true);
    setScheduleErr(null);
    fetch(apiUrl(`/api/faceit/team/${encodeURIComponent(teamId)}/upcoming-opponents?max=10&debug=1`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        if (!ok || j?.error) {
          setScheduleErr(j?.error || "Failed to load schedule");
          setScheduleDebug(j?.debug || null);
          return;
        }
        const payload = j as UpcomingOpponentsResp;
        setScheduleDebug(j?.debug || null);
        const mapped: ScheduleItem[] = (payload?.items || []).map((it, idx) => {
          const dt = it?.scheduledAt ? new Date(it.scheduledAt) : null;
          const date = dt ? dt.toLocaleDateString() : null;
          const time = dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
          const item: ScheduleItem = {
            id: idx,
            opponent: it?.opponentName || it?.opponentTeamId || "Opponent",
            opponentTeamId: it?.opponentTeamId || null,
            matchLink: it?.matchLink || null,
          };
          if (date) item.date = date;
          if (time) item.time = time;
          return item;
        });
        setSchedule(mapped);
      })
      .catch(() => mounted && setScheduleErr("Failed to load schedule"))
      .finally(() => mounted && setScheduleLoading(false));
    return () => {
      mounted = false;
    };
  }, [teamId]);

  // recent opponents now loaded alongside map pool for shared match set

  // Quick form estimate from rounds +/-
  const formSummary = useMemo(() => {
    if (!style) return { label: "--", tint: "text-muted" };
    const f = style.avgRoundsFor ?? 0;
    const a = style.avgRoundsAgainst ?? 0;
    const diff = f - a;
    if (style.sampleMaps === 0 || style.sampleMaps === null) return { label: "No recent maps", tint: "text-muted" };
    if (diff > 1.0) return { label: "Positive form", tint: "text-green-400" };
    if (diff < -1.0) return { label: "Struggling", tint: "text-red-400" };
    return { label: "Even form", tint: "text-yellow-400" };
  }, [style]);

  // Small bar for map win rate
  function MapBar({ pct }: { pct: number }) {
    const w = Math.max(0, Math.min(100, Math.round(pct)));
    return (
      <div className="w-full h-2 bg-surface rounded-full overflow-hidden border border-border">
        <div className="h-full bg-[#4FC3F7]" style={{ width: `${w}%` }} />
      </div>
    );
  }

  function RecentMapPool({ matches }: { matches: any[] }) {
    const mapAgg = new Map<
      string,
      { map: string; wins: number; total: number; winRate: number }
    >();
    for (const m of matches || []) {
      const map = m?.map || "Unknown";
      const prev = mapAgg.get(map) || { map, wins: 0, total: 0, winRate: 0 };
      const isWin = String(m?.result || "").toUpperCase() === "W";
      const total = prev.total + 1;
      const wins = prev.wins + (isWin ? 1 : 0);
      mapAgg.set(map, {
        map,
        wins,
        total,
        winRate: total ? Math.round((wins / total) * 100) : 0,
      });
    }
    const maps = Array.from(mapAgg.values()).sort((a, b) => b.total - a.total);

    if (!maps.length) {
      return <div className="text-xs text-muted">No matches found.</div>;
    }

    return (
      <div className="space-y-3">
        {maps.map((m) => (
          <div key={m.map} className="bg-bg border border-border rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <p className="font-medium">{m.map}</p>
              <p className="text-sm">{m.wins}-{m.total - m.wins}</p>
            </div>
            <div className="mt-2">
              <MapBar pct={m.winRate} />
            </div>
            <p className="mt-2 text-xs text-muted">
              {m.total} map{m.total !== 1 ? "s" : ""} played
            </p>
          </div>
        ))}
      </div>
    );
  }

  // Navigate to another teamId
  function onLoadAnother() {
    if (!queryId) return;
    navigate(`/faceit/intel/${encodeURIComponent(queryId)}`);
  }

  function onLoadOpponent(opponentTeamId?: string | null) {
    if (!opponentTeamId) return;
    setQueryId(opponentTeamId);
    navigate(`/faceit/intel/${encodeURIComponent(opponentTeamId)}`);
  }

  const userSnapshot = useMemo(() => {
    if (!user) return null;
    return {
      nickname: user.nickname ?? null,
      steamId: user.steamId ?? null,
      profileUrl: user.profileUrl ?? null,
      country: user.country ?? null,
      realname: (user as any)?.realname ?? null,
      picture: user.picture ?? null,
      avatar: user.avatar ?? null,
      avatarMedium: user.avatarMedium ?? null,
      avatarFull: user.avatarFull ?? null,
      faceit: {
        playerId: user.faceit?.playerId ?? null,
        nickname: user.faceit?.nickname ?? null,
        avatar: user.faceit?.avatar ?? null,
        country: user.faceit?.country ?? null,
        faceitUrl: user.faceit?.faceitUrl ?? null,
        game: {
          skillLevel: user.faceit?.game?.skillLevel ?? null,
          elo: user.faceit?.game?.elo ?? null,
        },
      },
      derived: {
        faceitTeamId: derivedTeamId ?? null,
        faceitTeamLoading: derivedTeamLoading,
        faceitTeamError: derivedTeamErr,
        faceitTeamSource: derivedTeamMeta?.source ?? null,
        faceitTeamConfidence: derivedTeamMeta?.confidence ?? null,
        faceitTeamSamples: derivedTeamMeta?.samples ?? null,
        faceitTeamCached: derivedTeamCached,
        leagueTeamId: derivedTeamMeta?.leagueTeamId ?? null,
        leagueTeamName: derivedTeamMeta?.leagueTeamName ?? null,
        leagueTeamConfidence: derivedTeamMeta?.leagueConfidence ?? null,
        leagueTeamSamples: derivedTeamMeta?.leagueSamples ?? null,
        analysisTeamId: derivedTeamMeta?.leagueTeamId ?? derivedTeamId ?? null,
      },
    };
  }, [user, derivedTeamId, derivedTeamLoading, derivedTeamErr, derivedTeamMeta, derivedTeamCached]);

  const diagnostics = useMemo(() => {
    return {
      routeTeamId: teamId ?? null,
      analysisTeamId: derivedTeamMeta?.leagueTeamId ?? derivedTeamId ?? null,
      mapPool: {
        maps: mappool?.maps?.length ?? 0,
        usedMatchIds: mappool?.usedMatchIds?.length ?? 0,
        diagnostics: mappool?.diagnostics ?? null,
      },
      keyPlayers: {
        players: keys?.players?.length ?? 0,
        usedMatchIds: keys?.usedMatchIds?.length ?? 0,
      },
      recentOpponents: {
        count: recentOpponents?.length ?? 0,
        error: recentOppErr ?? null,
      },
      recentOppDebug: recentOppDebug ?? null,
      schedule: {
        count: schedule?.length ?? 0,
        error: scheduleErr ?? null,
      },
      scheduleDebug: scheduleDebug ?? null,
      discoveryDebug: {
        error: discoveryDebugErr ?? null,
        data: discoveryDebug ?? null,
      },
      opponentMatchIds: {
        count: opponentMatchIds.length,
        ids: opponentMatchIds,
      },
      requests: {
        keyPlayers: keyPlayersRequestUrl,
        mapPool: mapPoolRequestUrl,
      },
      errors: {
        page: err ?? null,
        recent: recentErr ?? null,
      },
    };
  }, [
    teamId,
    derivedTeamMeta,
    derivedTeamId,
    mappool,
    keys,
    recentOpponents,
    recentOppErr,
    schedule,
    scheduleErr,
    err,
    recentErr,
  ]);

  useEffect(() => {
    const playerId = user?.faceit?.playerId;
    if (!playerId || !teamCacheKey) {
      setDerivedTeamId(null);
      setDerivedTeamErr(null);
      setDerivedTeamLoading(false);
      setDerivedTeamCached(false);
      return;
    }
    const ttlMs = 6 * 60 * 60 * 1000;
    try {
      const raw = localStorage.getItem(teamCacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.teamId && parsed?.ts && Date.now() - parsed.ts < ttlMs) {
          setDerivedTeamId(parsed.teamId);
          setDerivedTeamMeta(parsed.meta || null);
          setDerivedTeamCached(true);
          return;
        }
      }
    } catch {}
    let mounted = true;
    setDerivedTeamLoading(true);
    setDerivedTeamErr(null);
    setDerivedTeamCached(false);
    fetch(apiUrl(`/api/faceit/players/${encodeURIComponent(playerId)}/team`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        if (!ok || j?.error) {
          setDerivedTeamId(null);
          setDerivedTeamErr(j?.error || "Failed to resolve team");
          setDerivedTeamMeta(null);
          return;
        }
        const teamIdResolved = j?.leagueTeamId || j?.teamId || null;
        const meta = {
          source: j?.source,
          confidence: j?.confidence,
          samples: j?.samples,
          leagueTeamId: j?.leagueTeamId ?? null,
          leagueTeamName: j?.leagueTeamName ?? null,
          leagueConfidence: j?.leagueConfidence ?? null,
          leagueSamples: j?.leagueSamples ?? null,
        };
        setDerivedTeamId(teamIdResolved);
        setDerivedTeamMeta(meta);
        try {
          localStorage.setItem(teamCacheKey, JSON.stringify({ teamId: teamIdResolved, meta, ts: Date.now() }));
        } catch {}
      })
      .catch(() => mounted && setDerivedTeamErr("Failed to resolve team"))
      .finally(() => mounted && setDerivedTeamLoading(false));
    return () => {
      mounted = false;
    };
  }, [user?.faceit?.playerId, teamCacheKey]);

  useEffect(() => {
    if (!derivedTeamId) return;
    if (teamId) return;
    navigate(`/faceit/intel/${encodeURIComponent(derivedTeamId)}`, { replace: true });
  }, [derivedTeamId, teamId, navigate]);

  return (
    <div className="space-y-6">
      {/* Header / Search */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Opponent Intelligence</h1>
        <p className="text-sm text-muted">
          Deep dive on an opponent's roster, ELO, map pool, style, and key players (computed from public FACEIT data and your
          schedule source).
        </p>

        <div className="mt-2 flex items-center gap-2">
          <input
            value={queryId}
            onChange={(e) => setQueryId(e.target.value)}
            placeholder="Enter FACEIT team_id (uuid)"
            className="flex-1 bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
          />
          <button
            type="button"
            onClick={onLoadAnother}
            disabled={!queryId}
            className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-sm"
          >
            Load
          </button>
        </div>
      </div>

      <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent Opponents</h2>
          {recentOppLoading && <span className="text-xs text-muted">Loading...</span>}
        </div>
        {leagueContextLabel && (
          <div className="text-xs text-muted">
            League context:{" "}
            <span className="text-[#4FC3F7]">
              {leagueContextLabel}
              {recentOppDebug?.contextCached ? " (cached)" : ""}
            </span>
          </div>
        )}

        {recentOppErr && <div className="text-xs text-red-400">{recentOppErr}</div>}
        {!recentOppErr && !recentOppLoading && (!recentOpponents || recentOpponents.length === 0) && (
          <div className="text-xs text-muted">No recent opponents found.</div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          {(recentOpponents || []).map((op) => (
            <div key={`${op.matchId}-${op.opponentTeamId}`} className="bg-bg border border-border rounded-lg p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{op.opponentName || "Opponent"}</div>
                  <div className="text-xs text-muted">
                    {op.opponentTeamId || "--"}
                  </div>
                  <div className="text-xs text-muted">
                    {op.startedAt || "--"}{op.score ? ` • ${op.score}` : ""}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onLoadOpponent(op.opponentTeamId)}
                  disabled={!op.opponentTeamId}
                  className="px-2 py-1 rounded-md border border-border text-xs hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Load opponent
                </button>
                {op.matchLink && (
                  <a
                    href={op.matchLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#4FC3F7] hover:underline"
                  >
                    Match page
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {devToolsEnabled && (
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setActiveTab("intel")}
            className={`px-3 py-2 rounded-md border transition ${
              activeTab === "intel"
                ? "border-[#4FC3F7] text-[#4FC3F7]"
                : "border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7]"
            }`}
          >
            Intel
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("demos")}
            className={`px-3 py-2 rounded-md border transition ${
              activeTab === "demos"
                ? "border-[#4FC3F7] text-[#4FC3F7]"
                : "border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7]"
            }`}
          >
            Demos (Dev)
          </button>
        </div>
      )}

      {activeTab === "intel" && (
        <>
      <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Current User Snapshot</h2>
          <span className="text-xs text-muted">Live from /me</span>
        </div>
      {!userSnapshot && <div className="text-sm text-muted">No user data available.</div>}
      {userSnapshot && (
        <pre className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
{JSON.stringify(userSnapshot, null, 2)}
        </pre>
      )}
    </section>
        </>
      )}

      {devToolsEnabled && activeTab === "demos" && (
        <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Demo Lab</h2>
              <div className="text-xs text-muted">Select a demo, queue parse, and view outputs</div>
            </div>
            <span className="text-xs text-muted">Phase 2 local testing</span>
          </div>
          <div className="grid lg:grid-cols-3 gap-3">
            <label className="text-xs text-muted space-y-1">
              <span>Steam token</span>
              <input
                value={demoToken}
                onChange={(e) => setDemoToken(e.target.value)}
                placeholder="steam_token"
                className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
              />
            </label>
            <label className="text-xs text-muted space-y-1">
              <span>Match ID (optional)</span>
              <input
                value={demoMatchId}
                onChange={(e) => setDemoMatchId(e.target.value)}
                placeholder="1-xxxx"
                className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
              />
            </label>
            <label className="text-xs text-muted space-y-1">
              <span>Match URL (optional)</span>
              <input
                value={demoMatchUrl}
                onChange={(e) => setDemoMatchUrl(e.target.value)}
                placeholder="https://www.faceit.com/en/cs2/room/..."
                className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
              />
            </label>
            <label className="text-xs text-muted space-y-1">
              <span>Team ID (optional)</span>
              <input
                value={demoTeamId}
                onChange={(e) => setDemoTeamId(e.target.value)}
                placeholder="team id"
                className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
              />
            </label>
            <label className="text-xs text-muted space-y-1">
              <span>Demo URL (metadata only)</span>
              <input
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                placeholder="https://example.com/demo.dem"
                className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
              />
            </label>
            <div className="text-xs text-muted space-y-2 lg:col-span-2">
              <div className="flex items-center justify-between">
                <span>Upload demo file (local)</span>
                <span className="text-muted">Match ID optional</span>
              </div>
              <input
                ref={demoFileInputRef}
                type="file"
                accept=".dem"
                onChange={(e) => setDemoFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openDemoPicker}
                  className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
                >
                  Choose .dem file
                </button>
                <span className="text-xs text-muted truncate">
                  {demoFile ? demoFile.name : "No file selected"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button
              type="button"
              onClick={ingestDemo}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Ingest URL
            </button>
            <button
              type="button"
              onClick={uploadDemo}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Upload file
            </button>
            <button
              type="button"
              onClick={linkDemo}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Link demo
            </button>
            <button
              type="button"
              onClick={() => queueParse()}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Queue parse
            </button>
            <button
              type="button"
              onClick={loadEvents}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load events
            </button>
            <button
              type="button"
              onClick={loadRounds}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load rounds
            </button>
            <button
              type="button"
              onClick={loadMaps}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load maps
            </button>
            <button
              type="button"
              onClick={loadDemos}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load demos
            </button>
            <button
              type="button"
              onClick={loadDerivedStats}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load stats
            </button>
            <button
              type="button"
              onClick={loadPositionStats}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load positions
            </button>
            <button
              type="button"
              onClick={loadHeatmap}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Load heatmap
            </button>
            {demoStatus && <span className="text-muted">{demoStatus}</span>}
            {parseMeta && (
              <span className="text-muted">
                Auto worker: {parseMeta.enabled ? "on" : "off"}
                {parseMeta.attempted ? " (attempted)" : ""}
                {parseMeta.reason ? ` — ${parseMeta.reason}` : ""}
              </span>
            )}
          </div>
          {parseJobMeta && (
            <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
              <div className="text-xs text-muted uppercase tracking-wide">Parse meta</div>
              <pre className="text-[11px] text-muted overflow-auto">
                {JSON.stringify(parseJobMeta, null, 2)}
              </pre>
            </div>
          )}
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2 lg:col-span-2">
              <div className="text-xs text-muted uppercase tracking-wide">Available demos</div>
              {demoItems.length === 0 ? (
                <div className="text-muted">No demos loaded yet. Click “Load demos”.</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-2">
                  {demoItems.map((demo) => (
                    <div key={demo.id} className="border border-border/60 rounded-md p-3 bg-surface/60 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold">{demo.source || "DEMO"}</div>
                        <span className="text-[10px] text-muted">{demo.status || "unknown"}</span>
                      </div>
                      <div className="text-[11px] text-muted space-y-1">
                        <div>Demo ID: <span className="text-white">{demo.id}</span></div>
                        {demo.matchId && <div>Match: <span className="text-white">{demo.matchId}</span></div>}
                        {demo.fileUrl && <div className="truncate">File: <span className="text-white">{demo.fileUrl}</span></div>}
                        {demo.createdAt && (
                          <div>
                            Created: <span className="text-white">{new Date(demo.createdAt).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <label className="flex items-center gap-1 text-[11px] text-muted">
                          <input
                            type="checkbox"
                            checked={!!demoPurge[demo.id]}
                            onChange={(e) =>
                              setDemoPurge((prev) => ({ ...prev, [demo.id]: e.target.checked }))
                            }
                          />
                          Purge
                        </label>
                        <button
                          type="button"
                          onClick={() => selectDemo(demo)}
                          className="px-2 py-1 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-[11px]"
                        >
                          Select
                        </button>
                        <button
                          type="button"
                          onClick={() => selectDemo(demo, { parseAndLoad: true })}
                          className="px-2 py-1 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-[11px]"
                        >
                          Parse + Load
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteDemo(demo.id, !!demoPurge[demo.id])}
                          className="px-2 py-1 rounded-md border border-border bg-bg/60 hover:border-red-400 hover:text-red-400 transition text-[11px]"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
              <div className="text-xs text-muted uppercase tracking-wide">Quick actions</div>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={loadEvents}
                  className="px-3 py-2 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
                >
                  Load events
                </button>
                <button
                  type="button"
                  onClick={loadDerivedStats}
                  className="px-3 py-2 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
                >
                  Load stats
                </button>
                <button
                  type="button"
                  onClick={loadPositionStats}
                  className="px-3 py-2 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
                >
                  Load positions
                </button>
                <button
                  type="button"
                  onClick={loadHeatmap}
                  className="px-3 py-2 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
                >
                  Load heatmap
                </button>
              </div>
            </div>
          </div>
          <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
            <div className="text-xs text-muted uppercase tracking-wide">Dev: Grant team access</div>
            <div className="grid md:grid-cols-2 gap-2">
              <label className="text-xs text-muted space-y-1">
                <span>User ID</span>
                <input
                  value={adminUserId}
                  onChange={(e) => setAdminUserId(e.target.value)}
                  placeholder={currentUserId || "user id"}
                  className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
                />
              </label>
              <label className="text-xs text-muted space-y-1">
                <span>Team ID</span>
                <input
                  value={adminTeamId}
                  onChange={(e) => setAdminTeamId(e.target.value)}
                  placeholder={teamId || "team id"}
                  className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={grantTeamAccess}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
            >
              Grant access (OWNER)
            </button>
          </div>
          <label className="text-xs text-muted space-y-1">
            <span>Demo ID to link (use Load demos)</span>
            <input
              value={linkDemoId}
              onChange={(e) => setLinkDemoId(e.target.value)}
              placeholder="demo id"
              className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
            />
          </label>
          <label className="text-xs text-muted space-y-1">
            <span>Demo ID to parse</span>
            <input
              value={parseDemoId}
              onChange={(e) => setParseDemoId(e.target.value)}
              placeholder="demo id"
              className="w-full bg-bg border border-border rounded-md px-3 py-2 text-xs"
            />
          </label>
          <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
            <div className="text-xs text-muted uppercase tracking-wide">Results summary</div>
            <div className="grid md:grid-cols-2 gap-2">
              <div className="text-xs text-muted space-y-1">
                <div>Events (loaded)</div>
                <div className="text-white">{eventItems.length}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Rounds</div>
                <div className="text-white">{roundItems.length}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Maps</div>
                <div className="text-white">{mapItems.length}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Total events</div>
                <div className="text-white">{eventTotal ?? "—"}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Position frames</div>
                <div className="text-white">{positionCount ?? "—"}</div>
              </div>
            </div>
            {eventStats.length > 0 && (
              <div className="grid md:grid-cols-2 gap-2 pt-2">
                {eventStats
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 8)
                  .map((row) => (
                    <div key={row.type} className="text-xs text-muted flex items-center justify-between">
                      <span>{row.type}</span>
                      <span className="text-white">{row.count}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted uppercase tracking-wide">Diagnostics</div>
              <button
                type="button"
                onClick={loadPositionDebug}
                className="px-2 py-1 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-[11px]"
              >
                Refresh diagnostics
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-2">
              <div className="text-xs text-muted space-y-1">
                <div>Rounds loaded</div>
                <div className="text-white">{roundItems.length}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Positions total</div>
                <div className="text-white">{positionDebug?.total ?? "—"}</div>
              </div>
              <div className="text-xs text-muted space-y-1">
                <div>Positions with coords</div>
                <div className="text-white">{positionDebug?.withCoords ?? "—"}</div>
              </div>
            </div>
            {positionDebug?.sample && (
              <pre className="text-[11px] text-muted overflow-auto">
{JSON.stringify(positionDebug.sample, null, 2)}
              </pre>
            )}
          </div>
          <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted uppercase tracking-wide">Parse meta</div>
              <button
                type="button"
                onClick={loadParseJobMeta}
                className="px-2 py-1 rounded-md border border-border bg-bg/60 hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-[11px]"
              >
                Refresh parse meta
              </button>
            </div>
            {parseJobMeta ? (
              <pre className="text-[11px] text-muted overflow-auto">
{JSON.stringify(parseJobMeta, null, 2)}
              </pre>
            ) : (
              <div className="text-[11px] text-muted">
                No parse meta yet. Click Refresh parse meta after running a parse.
              </div>
            )}
          </div>
          <div className="bg-bg border border-border rounded-md p-3 text-xs space-y-3">
            <div className="text-xs text-muted uppercase tracking-wide">Heatmap</div>
            <div className="grid md:grid-cols-4 gap-2">
              <label className="text-xs text-muted space-y-1">
                <span>Player (optional)</span>
                <select
                  value={heatmapPlayerId}
                  onChange={(e) => setHeatmapPlayerId(e.target.value)}
                  className="w-full bg-bg border border-border rounded-md px-2 py-1 text-xs"
                >
                  <option value="">All players</option>
                  {heatmapPlayersForSelect.map((p) => (
                    <option key={p.steamId} value={p.steamId}>
                      {p.name}
                      {p.count === 0 ? " (no positions)" : ""}
                    </option>
                  ))}
                </select>
                {heatmapPlayersForSelect.length === 0 && (
                  <div className="text-[10px] text-muted">Load events to populate names.</div>
                )}
              </label>
              <label className="text-xs text-muted space-y-1">
                <span>Map (optional)</span>
                <input
                  value={heatmapMapName}
                  onChange={(e) => setHeatmapMapName(e.target.value)}
                  placeholder="de_mirage"
                  className="w-full bg-bg border border-border rounded-md px-2 py-1 text-xs"
                />
              </label>
              <label className="text-xs text-muted space-y-1">
                <span>Detail</span>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={heatmapDetail}
                  onChange={(e) => setHeatmapDetail(Number(e.target.value))}
                  className="w-full"
                />
                <div className="text-[10px] text-muted">Low -&gt; High</div>
              </label>
              <label className="text-xs text-muted space-y-1">
                <span>Limit</span>
                <input
                  value={heatmapLimit}
                  onChange={(e) => setHeatmapLimit(e.target.value)}
                  placeholder="20000"
                  className="w-full bg-bg border border-border rounded-md px-2 py-1 text-xs"
                />
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-4 items-start">
              <div className="bg-surface border border-border rounded-md p-2">
                <div className="flex items-center justify-between text-[11px] text-muted mb-2">
                  <span className="inline-flex items-center gap-1">
                    <span aria-hidden="true">🗺️</span>
                    <span>Map</span>
                  </span>
                  <span className="text-white">
                    {heatmapMapName || heatmapData?.mapName || "unknown"}
                  </span>
                </div>
                {(() => {
                  const imgW = heatmapData?.stats?.imgWidth || 200;
                  const imgH = heatmapData?.stats?.imgHeight || 200;
                  return (
                    <div className="relative w-full" style={{ aspectRatio: `${imgW}/${imgH}` }}>
                      <img
                        src={`/maps/${(heatmapMapName || heatmapData?.mapName || "de_dust2")
                          .toString()
                          .trim()
                          .toLowerCase()}.png`}
                        alt="Map overview"
                        className="absolute inset-0 w-full h-full object-contain opacity-90"
                      />
                      <canvas
                        ref={heatmapCanvasRef}
                        width={imgW}
                        height={imgH}
                        className="absolute inset-0 w-full h-full z-10"
                      />
                    </div>
                  );
                })()}
                <div className="text-[11px] text-muted mt-2">
                  {heatmapData?.stats?.points ? `Points: ${heatmapData.stats.points}` : "No data"}
                </div>
                <div className="text-[10px] text-muted">
                  Frames loaded: {positionCount ?? "—"} • Auto-refresh on filter change
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-xs text-muted">Top clusters</div>
                {Array.isArray(heatmapData?.top) && heatmapData.top.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead className="text-muted">
                      <tr>
                        <th className="text-left py-1">Bin</th>
                        <th className="text-right py-1">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {heatmapData.top.map((b: any) => (
                        <tr key={`${b.x}-${b.y}`} className="border-t border-border/40">
                          <td className="py-1 pr-2">{b.x},{b.y}</td>
                          <td className="py-1 text-right">{b.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-xs text-muted">No bins yet.</div>
                )}
              </div>
            </div>
          </div>
          {eventItems.length > 0 && (
            <div className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
              <div className="text-xs text-muted mb-2">Events (sample)</div>
              <pre>{JSON.stringify(eventItems, null, 2)}</pre>
            </div>
          )}
          {roundItems.length > 0 && (
            <div className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
              <div className="text-xs text-muted mb-2">Rounds</div>
              <pre>{JSON.stringify(roundItems, null, 2)}</pre>
            </div>
          )}
          {mapItems.length > 0 && (
            <div className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
              <div className="text-xs text-muted mb-2">Maps</div>
              <pre>{JSON.stringify(mapItems, null, 2)}</pre>
            </div>
          )}
          {derivedStats?.players && (
            <div className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
              <div className="text-xs text-muted mb-2">
                Derived stats (approx) • Rounds: {derivedStats?.totalRounds ?? 0}
              </div>
              <table className="w-full text-xs">
                <thead className="text-muted">
                  <tr>
                    <th className="text-left py-1">SteamID</th>
                    <th className="text-right py-1">K</th>
                    <th className="text-right py-1">D</th>
                    <th className="text-right py-1">A</th>
                    <th className="text-right py-1">EK</th>
                    <th className="text-right py-1">ED</th>
                    <th className="text-right py-1">TR</th>
                    <th className="text-right py-1">CL</th>
                    <th className="text-right py-1">ADR</th>
                    <th className="text-right py-1">KAST*</th>
                    <th className="text-right py-1">Entry%</th>
                    <th className="text-right py-1">Trade%</th>
                    <th className="text-right py-1">Clutch%</th>
                  </tr>
                </thead>
                <tbody>
                  {derivedStats.players
                    .slice()
                    .sort((a: any, b: any) => (b.kills || 0) - (a.kills || 0))
                    .slice(0, 12)
                    .map((p: any) => (
                      <tr key={p.steamId} className="border-t border-border/40">
                        <td className="py-1 pr-2">{p.steamId}</td>
                        <td className="py-1 text-right">{p.kills}</td>
                        <td className="py-1 text-right">{p.deaths}</td>
                        <td className="py-1 text-right">{p.assists}</td>
                        <td className="py-1 text-right">{p.entryKills ?? 0}</td>
                        <td className="py-1 text-right">{p.entryDeaths ?? 0}</td>
                        <td className="py-1 text-right">{p.trades ?? 0}</td>
                        <td className="py-1 text-right">
                          {p.clutchWins ?? 0}/{p.clutchAttempts ?? 0}
                        </td>
                        <td className="py-1 text-right">{p.adr}</td>
                        <td className="py-1 text-right">
                          {typeof p.kastApprox === "number"
                            ? `${Math.round(p.kastApprox * 100)}%`
                            : "—"}
                        </td>
                        <td className="py-1 text-right">
                          {(() => {
                            const total = (p.entryKills ?? 0) + (p.entryDeaths ?? 0);
                            return total ? `${Math.round((p.entryKills / total) * 100)}%` : "—";
                          })()}
                        </td>
                        <td className="py-1 text-right">
                          {(() => {
                            const total = (p.trades ?? 0) + (p.tradedDeaths ?? 0);
                            return total ? `${Math.round((p.trades / total) * 100)}%` : "—";
                          })()}
                        </td>
                        <td className="py-1 text-right">
                          {(() => {
                            const total = p.clutchAttempts ?? 0;
                            return total ? `${Math.round((p.clutchWins / total) * 100)}%` : "—";
                          })()}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {Array.isArray(derivedStats?.teams) && derivedStats.teams.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-muted mb-1">Team stats (approx)</div>
                  <table className="w-full text-xs">
                    <thead className="text-muted">
                      <tr>
                        <th className="text-left py-1">Team</th>
                        <th className="text-right py-1">OpenW</th>
                        <th className="text-right py-1">OpenL</th>
                        <th className="text-right py-1">Trades</th>
                        <th className="text-right py-1">Traded</th>
                        <th className="text-right py-1">Clutch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {derivedStats.teams.map((t: any) => (
                        <tr key={t.team} className="border-t border-border/40">
                          <td className="py-1 pr-2">{t.team}</td>
                          <td className="py-1 text-right">{t.openingDuelsWon ?? 0}</td>
                          <td className="py-1 text-right">{t.openingDuelsLost ?? 0}</td>
                          <td className="py-1 text-right">{t.tradeKills ?? 0}</td>
                          <td className="py-1 text-right">{t.tradedDeaths ?? 0}</td>
                          <td className="py-1 text-right">
                            {t.clutchWins ?? 0}/{t.clutchAttempts ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {Array.isArray(derivedStats?.notes) && derivedStats.notes.length > 0 && (
                <div className="text-muted mt-2 space-y-1">
                  {derivedStats.notes.map((n: string, i: number) => (
                    <div key={`${n}-${i}`}>• {n}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Diagnostics</h2>
          <span className="text-xs text-muted">Live counts</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowDiscoveryFallback}
              onChange={(e) => setAllowDiscoveryFallback(e.target.checked)}
              className="accent-[#4FC3F7]"
            />
            Allow discovery fallback when no recent opponent matches
          </label>
        </div>
        {leagueContextLabel && (
          <div className="text-xs text-muted">
            League context:{" "}
            <span className="text-[#4FC3F7]">
              {leagueContextLabel}
              {recentOppDebug?.contextCached ? " (cached)" : ""}
            </span>
          </div>
        )}
        <div className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto space-y-2">
          <div className="text-xs text-muted">Discovery (stats sample)</div>
          {discoveryDebugErr && <div className="text-red-400">{discoveryDebugErr}</div>}
          {!discoveryDebugErr && !discoveryDebug && (
            <div className="text-muted">Loading discovery debug...</div>
          )}
          {discoveryDebug && (
            <div className="space-y-2">
              {[
                { key: "league", label: "League Scrape" },
                { key: "rosterHistoryLeague", label: "Roster League" },
                { key: "teamPage", label: "Team Page" },
                { key: "rosterHistory", label: "Roster History" },
              ].map((row) => {
                const src = discoveryDebug?.[row.key];
                const stats = src?.statsSample;
                return (
                  <div
                    key={row.key}
                    className="grid grid-cols-[140px_1fr] gap-3 border border-border rounded-md px-3 py-2"
                  >
                    <div className="text-muted">{row.label}</div>
                    <div className="flex flex-wrap gap-3">
                      <span>count: {src?.count ?? 0}</span>
                      <span>
                        stats: {stats ? `${stats.statsAvailable}/${stats.sampleSize}` : "--"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <pre className="bg-bg border border-border rounded-md p-3 text-xs overflow-auto">
{JSON.stringify(diagnostics, null, 2)}
        </pre>
        <div className="border-t border-border pt-3 space-y-2">
          <div className="text-xs text-muted">Admin-only actions</div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={cacheToken}
              onChange={(e) => setCacheToken(e.target.value)}
              placeholder="Cache clear token (if required)"
              className="flex-1 bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
            />
            <button
              type="button"
              onClick={() => {
                setCacheClearStatus("Clearing...");
                const init: RequestInit = { method: "POST" };
                if (cacheToken) {
                  init.headers = { "x-cache-token": cacheToken };
                }
                fetch(apiUrl("/api/faceit/cache/clear"), init)
                  .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
                  .then(({ ok, j }) => {
                    if (!ok || j?.error) {
                      setCacheClearStatus(j?.error || "Failed to clear cache");
                      return;
                    }
                    setCacheClearStatus("Cache cleared.");
                  })
                  .catch(() => setCacheClearStatus("Failed to clear cache"));
              }}
              className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-sm"
            >
              Clear Faceit Cache
            </button>
          </div>
          {cacheClearStatus && <div className="text-xs text-muted">{cacheClearStatus}</div>}
        </div>
      </section>

      <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upcoming Opponents</h2>
          {scheduleLoading && <span className="text-xs text-muted">Loading...</span>}
        </div>

        {scheduleErr && <div className="text-xs text-red-400">{scheduleErr}</div>}
        {!scheduleErr && !scheduleLoading && (!schedule || schedule.length === 0) && (
          <div className="text-xs text-muted">No upcoming matches found for this team.</div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          {(schedule || []).map((fx) => (
            <div key={`${fx.id}-${fx.opponent}`} className="bg-bg border border-border rounded-lg p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{fx.opponent}</div>
                  <div className="text-xs text-muted">
                    {(fx.date || "--") + (fx.time ? ` ${fx.time}` : "")}
                  </div>
                  <div className="text-xs text-muted">{fx.division || "League"}</div>
                </div>
                <div className="text-right text-xs text-muted">
                  <div>Your ELO: {fx.yourElo ?? "--"}</div>
                  <div>Opp ELO: {fx.opponentElo ?? "--"}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onLoadOpponent(fx.opponentTeamId)}
                  disabled={!fx.opponentTeamId}
                  className="px-2 py-1 rounded-md border border-border text-xs hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Load opponent
                </button>
                {fx.matchLink && (
                  <a
                    href={fx.matchLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#4FC3F7] hover:underline"
                  >
                    Match page
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Loading / Error */}
      {loading && <div className="text-sm text-muted">Loading opponent intelligence...</div>}
      {err && !loading && <div className="text-sm text-red-400">{err}</div>}

      {/* Content */}
      {!loading && !err && (
        <div className="space-y-6">
          <div className="grid xl:grid-cols-2 gap-6">
            {/* LEFT COLUMN: Form/ELO + Style */}
            <div className="flex flex-col gap-6">
              {/* Form / ELO */}
              <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
                <h2 className="text-lg font-semibold">Form & ELO</h2>
                {/* Team header */}
                <div className="flex items-center gap-3">
                  {profile?.team?.avatar && (
                    <img
                      src={profile.team.avatar}
                      alt={profile.team.name}
                      className="w-12 h-12 rounded-md border border-border object-cover"
                    />
                  )}
                  <div>
                    <p className="font-semibold">{profile?.team?.name ?? "--"}</p>
                    <p className="text-xs text-muted">{profile?.team?.team_id}</p>
                  </div>
                </div>

                {/* ELO + form */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted">Team Avg ELO</p>
                    <p className="font-semibold">{profile?.team_avg_elo ?? "--"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Sample (maps)</p>
                    <p className="font-semibold">{style?.sampleMaps ?? "--"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Form</p>
                    <p className={`font-semibold ${formSummary.tint}`}>{formSummary.label}</p>
                  </div>
                </div>

                {/* Rounds differential */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted">Avg Rounds Won / Map</p>
                    <p className="font-semibold">
                      {typeof style?.avgRoundsFor === "number" ? style.avgRoundsFor.toFixed(2) : "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Avg Rounds Lost / Map</p>
                    <p className="font-semibold">
                      {typeof style?.avgRoundsAgainst === "number" ? style.avgRoundsAgainst.toFixed(2) : "--"}
                    </p>
                  </div>
                </div>
              </section>

              {/* Style */}
              <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
                <h2 className="text-lg font-semibold">Style (Safe Subset)</h2>
                <p className="text-xs text-muted">
                  Stable indicators computed from recent match stats. Additional metrics (pace, opening duels) can be added when
                  available consistently.
                </p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted">Sample (maps)</p>
                    <p className="font-semibold">{style?.sampleMaps ?? "--"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Avg Rounds Won / Map</p>
                    <p className="font-semibold">
                      {typeof style?.avgRoundsFor === "number" ? style.avgRoundsFor.toFixed(2) : "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Avg Rounds Lost / Map</p>
                    <p className="font-semibold">
                      {typeof style?.avgRoundsAgainst === "number" ? style.avgRoundsAgainst.toFixed(2) : "--"}
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* MIDDLE COLUMN: Map Pool */}
            <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
              <h2 className="text-lg font-semibold">Map Pool (last ~20 matches)</h2>
              <p className="text-xs text-muted">
                Win rate and sample by map. Use this to steer veto toward comfort maps and away from low-sample traps.
              </p>

              {!mappool?.maps?.length && <p className="text-sm text-muted">No map data available.</p>}

              <div className="space-y-3">
                {mappool?.maps?.map((m) => (
                  <div key={m.map} className="bg-bg border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <p className="font-medium">{m.map}</p>
                      <p className="text-sm">{m.wins ?? 0}-{(m.total ?? m.sampleSize ?? 0) - (m.wins ?? 0)}</p>
                    </div>
                    <div className="mt-2">
                      <MapBar pct={m.winRate} />
                    </div>
                    <p className="mt-2 text-xs text-muted">
                      {m.total ?? m.sampleSize ?? 0} map{(m.total ?? m.sampleSize ?? 0) !== 1 ? "s" : ""} played
                    </p>
                    {m.matchIds && m.matchIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.matchIds.map((id: string, idx: number) => (
                          <a
                            key={`${m.map}-${id}`}
                            href={`https://www.faceit.com/en/cs2/room/${id}/scoreboard`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-[#4FC3F7] hover:underline"
                          >
                            Match Page {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Key Players - full width below Form & ELO + Map Pool */}
          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <h2 className="text-lg font-semibold">Key Players</h2>
            <p className="text-xs text-muted">
              Top performers by KPR/KD/HS%. Click names in Team Profile for deeper review if needed.
            </p>

            {!keys?.players?.length && <p className="text-sm text-muted">No player stats available.</p>}

            <div className="space-y-2">
              {keys?.players?.map((p) => (
                <div
                  key={p.nickname}
                  className="bg-bg border border-border rounded-lg p-3 flex items-center justify-between text-sm"
                >
                  <div className="flex-1">
                    <a
                      href={`/faceit/search?nickname=${encodeURIComponent(p.nickname)}`}
                      className="font-semibold hover:text-[#4FC3F7]"
                      title={p.player_id || undefined}
                    >
                      {p.nickname}
                    </a>
                    <p className="text-xs text-muted">{p.matches} match{p.matches !== 1 ? "es" : ""}</p>
                  </div>
                  <div className="grid grid-cols-5 gap-4">
                    <div>
                      <p className="text-xs text-muted">KPR</p>
                      <p className="font-semibold">{p.kpr.toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted">K/D</p>
                      <p className="font-semibold">{p.kd.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted">HS%</p>
                      <p className="font-semibold">{p.hsPct}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted">Kills</p>
                      <p className="font-semibold">{p.kills ?? "--"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted">Deaths</p>
                      <p className="font-semibold">{p.deaths ?? "--"}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-5 gap-4 text-xs text-muted">
                    <div>
                      <p>ADR</p>
                      <p className="text-sm font-semibold text-text">{p.adr ?? "--"}</p>
                    </div>
                    <div>
                      <p>Assists</p>
                      <p className="text-sm font-semibold text-text">{p.assists ?? "--"}</p>
                    </div>
                    <div>
                      <p>MVPs</p>
                      <p className="text-sm font-semibold text-text">{p.mvps ?? "--"}</p>
                    </div>
                    <div>
                      <p>First Kills</p>
                      <p className="text-sm font-semibold text-text">{p.firstKills ?? "--"}</p>
                    </div>
                    <div>
                      <p>Clutch Kills</p>
                      <p className="text-sm font-semibold text-text">{p.clutchKills ?? "--"}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-5 gap-4 text-xs text-muted">
                    <div>
                      <p>Entry W/L</p>
                      <p className="text-sm font-semibold text-text">
                        {p.entryWins ?? "--"} / {p.entryCount ?? "--"}
                      </p>
                    </div>
                    <div>
                      <p>Utility Dmg</p>
                      <p className="text-sm font-semibold text-text">{p.utilityDamage ?? "--"}</p>
                    </div>
                    <div>
                      <p>Flashes</p>
                      <p className="text-sm font-semibold text-text">{p.flashCount ?? "--"}</p>
                    </div>
                    <div>
                      <p>Enemies Flashed</p>
                      <p className="text-sm font-semibold text-text">{p.enemiesFlashed ?? "--"}</p>
                    </div>
                    <div>
                      <p>Sniper Kills</p>
                      <p className="text-sm font-semibold text-text">{p.sniperKills ?? "--"}</p>
                    </div>
                  </div>
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-[#4FC3F7]">Raw stats (sample)</summary>
                    <pre className="mt-2 bg-bg border border-border rounded-md p-2 overflow-auto text-[11px]">
{JSON.stringify(p.rawStats || {}, null, 2)}
                    </pre>
                    <div className="text-[11px] text-muted mt-1">
                      Samples: {p.rawStatsSamples ?? 0}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </section>

          <FullDetails data={{ profile, mappool, style, keys }} title="All fields" />

          <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Recent Team Summary (Last 20)</h2>
              <button
                type="button"
                onClick={() => setShowRecent((v) => !v)}
                className="px-3 py-1.5 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-xs"
              >
                {showRecent ? "Hide" : "Show"}
              </button>
            </div>

            {showRecent && (
              <>
                {recentErr && <div className="text-xs text-red-400">{recentErr}</div>}
                {!recentErr && recentSummary === null && (
                  <div className="text-xs text-muted">Loading recent summary...</div>
                )}
                {recentSummary && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-bg border border-border rounded-lg p-3">
                        <div className="text-xs text-muted">Wins</div>
                        <div className="text-lg font-semibold">{recentSummary.wins ?? 0}</div>
                      </div>
                      <div className="bg-bg border border-border rounded-lg p-3">
                        <div className="text-xs text-muted">Losses</div>
                        <div className="text-lg font-semibold">{recentSummary.losses ?? 0}</div>
                      </div>
                      <div className="bg-bg border border-border rounded-lg p-3">
                        <div className="text-xs text-muted">Winrate</div>
                        <div className="text-lg font-semibold">{recentSummary.winrate ?? 0}%</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(recentSummary.players || []).map((p: any) => (
                        <div
                          key={p.player_id || p.nickname}
                          className="bg-bg border border-border rounded-lg p-3 flex items-center justify-between text-sm"
                        >
                          <div className="flex-1">
                            <a
                              href={`/faceit/search?nickname=${encodeURIComponent(p.nickname)}`}
                              className="font-semibold hover:text-[#4FC3F7]"
                            >
                              {p.nickname}
                            </a>
                            <p className="text-xs text-muted">{p.matches} match{p.matches !== 1 ? "es" : ""}</p>
                          </div>
                          <div className="grid grid-cols-5 gap-4">
                            <div>
                              <p className="text-xs text-muted">KPR</p>
                              <p className="font-semibold">{p.kpr}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">K/D</p>
                              <p className="font-semibold">{p.kd}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">HS%</p>
                              <p className="font-semibold">{p.hsPct}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">K-D</p>
                              <p className="font-semibold">{p.kills}-{p.deaths}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">Winrate</p>
                              <p className="font-semibold">{p.winrate ?? 0}%</p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(recentSummary.players || []).length === 0 && (
                        <div className="text-xs text-muted">No player stats found.</div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs text-muted uppercase tracking-wide">Recent Map Win Rate (Last 20)</div>
                      <RecentMapPool matches={recentSummary.matches || []} />
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
